<template>
  <div class="filtro-dropdown-generico-wrapper" :style="customStyle">
    <label v-if="label" class="filtro-dropdown-label">{{ label }}</label>
    
    <el-select
      ref="selectRef"
      :value="localValue"
      @input="onInput"
      @change="onChange"
      @clear="onClear"
      :placeholder="placeholder"
      :filterable="filterable"
      :clearable="clearable"
      :size="size"
      :disabled="disabled"
      :multiple="multiple"
      :collapse-tags="collapseTags"
      :popper-append-to-body="popperAppendToBody"
      popper-class="filtro-dropdown-apple-popper"
      class="filtro-dropdown-select"
    >
      <!-- Prefix Slot -->
      <template slot="prefix" v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>

      <!-- Options -->
      <el-option
        v-for="(item, idx) in normalizedOptions"
        :key="item.key || item.value || idx"
        :value="item.value"
        :label="item.label"
        :disabled="item.disabled"
      >
        <!-- Custom Scoped Slot or Default Item Renderer -->
        <slot name="option" :option="item.raw" :item="item">
          <div class="filtro-option-row">
            <!-- Auto Icon for payment/banks/persons via generic AppIcon -->
            <AppIcon
              v-if="showIcons && item.meta && item.meta.icon"
              :name="item.meta.icon"
              :size="14"
              :color="item.meta.color || '#64748b'"
              class="filtro-option-icon"
            />
            <span class="filtro-option-text">{{ item.label }}</span>
          </div>
        </slot>
      </el-option>
    </el-select>
  </div>
</template>

<script>
export default {
  name: "FiltroDropdownGenerico",
  props: {
    value: {
      type: [String, Number, Array],
      default: null
    },
    label: {
      type: String,
      default: ""
    },
    options: {
      type: Array,
      default: () => []
    },
    valueKey: {
      type: String,
      default: "id"
    },
    labelKey: {
      type: String,
      default: "nome"
    },
    placeholder: {
      type: String,
      default: "Selecione"
    },
    clearable: {
      type: Boolean,
      default: true
    },
    filterable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    collapseTags: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: "small"
    },
    popperAppendToBody: {
      type: Boolean,
      default: false
    },
    showIcons: {
      type: Boolean,
      default: true
    },
    width: {
      type: String,
      default: null
    },
    minWidth: {
      type: String,
      default: "160px"
    }
  },
  data() {
    return {
      localValue: this.value
    };
  },
  computed: {
    customStyle() {
      const style = {};
      if (this.width) style.width = this.width;
      if (this.minWidth) style.minWidth = this.minWidth;
      return style;
    },
    normalizedOptions() {
      if (!this.options || !Array.isArray(this.options)) return [];

      return this.options.map((opt, index) => {
        if (opt === null || opt === undefined) {
          return { value: null, label: "", raw: opt };
        }

        // Se for tipo primitivo (string ou number)
        if (typeof opt !== "object") {
          return {
            value: opt,
            label: String(opt),
            key: opt + "_" + index,
            raw: opt,
            meta: this.detectOptionMeta(String(opt))
          };
        }

        // Se for objeto
        const val = typeof opt[this.valueKey] !== "undefined"
          ? opt[this.valueKey]
          : (typeof opt.value !== "undefined" ? opt.value : (typeof opt.id !== "undefined" ? opt.id : opt));

        let lbl = "";
        if (typeof opt[this.labelKey] !== "undefined" && opt[this.labelKey] !== null) {
          lbl = opt[this.labelKey];
        } else if (typeof opt.nome !== "undefined" && opt.nome !== null) {
          lbl = opt.nome;
        } else if (typeof opt.razao_social !== "undefined" && opt.razao_social !== null) {
          lbl = opt.razao_social;
        } else if (typeof opt.nome_fantasia !== "undefined" && opt.nome_fantasia !== null) {
          lbl = opt.nome_fantasia;
        } else if (typeof opt.label !== "undefined" && opt.label !== null) {
          lbl = opt.label;
        } else {
          lbl = String(val);
        }

        return {
          value: val,
          label: lbl,
          disabled: Boolean(opt.disabled),
          key: (val !== null && val !== undefined ? String(val) : "") + "_" + index,
          raw: opt,
          meta: this.detectOptionMeta(lbl)
        };
      });
    }
  },
  watch: {
    value(val) {
      this.localValue = val;
    }
  },
  methods: {
    onInput(val) {
      this.localValue = val;
      this.$emit("input", val);
    },
    onChange(val) {
      this.localValue = val;
      this.$emit("input", val);
      const selected = this.normalizedOptions.find(o => String(o.value) === String(val));
      this.$emit("change", val, selected ? selected.raw : null);
    },
    onClear() {
      this.localValue = null;
      this.$emit("input", null);
      this.$emit("change", null, null);
      this.$emit("clear");
    },
    detectOptionMeta(label) {
      if (!label) return null;
      const str = String(label).toUpperCase();

      if (str.includes("BOLETO")) {
        return { icon: "barcode", color: "#0284c7" };
      }
      if (str.includes("PIX")) {
        return { icon: "pix", color: "#00bba2" };
      }
      if (str.includes("CARTAO") || str.includes("CARTÃO") || str.includes("CREDITO") || str.includes("CRÉDITO") || str.includes("DEBITO") || str.includes("DÉBITO")) {
        return { icon: "card", color: "#7e22ce" };
      }
      if (str.includes("DINHEIRO") || str.includes("ESPECIE") || str.includes("ESPÉCIE")) {
        return { icon: "cash", color: "#047857" };
      }
      if (str.includes("TRANSF") || str.includes("TED") || str.includes("DOC") || str.includes("DEPOSITO") || str.includes("DEPÓSITO")) {
        return { icon: "bank", color: "#4338ca" };
      }
      if (str.includes("EMPENHO")) {
        return { icon: "bank", color: "#0891b2" };
      }
      if (str.includes("RECORRENTE") || str.includes("PAGSEGURO") || str.includes("PERMUTA")) {
        return { icon: "sync", color: "#d97706" };
      }
      if (str.includes("PESSOA FÍSICA") || str.includes("PESSOA FISICA")) {
        return { icon: "user", color: "#0284c7" };
      }
      if (str.includes("PESSOA JURÍDICA") || str.includes("PESSOA JURIDICA")) {
        return { icon: "building", color: "#7c3aed" };
      }
      return null;
    }
  }
};
</script>

<style scoped>
.filtro-dropdown-generico-wrapper {
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0;
}

.filtro-dropdown-label {
  color: #1e293b;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0 0 6px 2px;
  line-height: 1.2;
  height: 16px;
  white-space: nowrap;
}

.filtro-dropdown-select {
  width: 100%;
}

.filtro-dropdown-select >>> .el-input__inner {
  height: 34px !important;
  line-height: 34px !important;
  border-radius: 8px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  color: #1e293b !important;
  font-size: 12.5px !important;
  font-weight: 500 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

.filtro-dropdown-select >>> .el-input__icon {
  line-height: 34px !important;
}

.filtro-dropdown-select >>> .el-input__inner:hover {
  border-color: #cbd5e1 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06) !important;
}

.filtro-dropdown-select >>> .el-input__inner:focus {
  border-color: #00bba2 !important;
  box-shadow: 0 0 0 3px rgba(0, 187, 162, 0.15) !important;
}

.filtro-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filtro-option-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.filtro-option-text {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}
</style>
